import {useEffect, useRef, type FocusEvent} from 'react'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import {Image, LogoSuite} from '@primer/react-brand/esm'

import type {GenericContent} from '../../../../../brand/lib/types/contentful'
import styles from './CopilotLogoSuite.module.css'

type Props = {
  component: GenericContent
  location: string
}

// Primer's marquee stops its animation (resetting the position) on any focus. That is meant for
// keyboard users, but a mouse press also focuses the link and moves it out from under the cursor
// before the click completes. Hide pointer-initiated focus from Primer; it still pauses via :focus-within.
// Remove again once https://github.com/primer/brand/issues/1518 is fixed
function stopPointerFocusPropagation(event: FocusEvent<HTMLAnchorElement>) {
  if (!event.currentTarget.matches(':focus-visible')) {
    event.stopPropagation()
  }
}

export function CopilotLogoSuite({component, location}: Props) {
  const {heading, media} = component.fields
  const ref = useRef<HTMLDivElement>(null)

  // Hotfix to remove inert and aria-hidden on the second logo group since we use links
  // This hotfix can be removed again once https://github.com/primer/brand/issues/1517 is fixed

  useEffect(() => {
    const root = ref.current
    if (!root) return
    const clear = () => {
      for (const el of root.querySelectorAll('[inert]')) {
        el.removeAttribute('inert')
        el.removeAttribute('aria-hidden')
      }
    }
    clear()
    // Re-apply in case the component re-renders and sets it again
    const observer = new MutationObserver(clear)
    observer.observe(root, {attributes: true, attributeFilter: ['inert'], subtree: true})
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={ref}>
      <LogoSuite className={styles.CopilotLogoSuite} align="center" hasDivider={false}>
        <LogoSuite.Heading>{heading ?? t('Copilot integrations')}</LogoSuite.Heading>
        <LogoSuite.Logobar marquee marqueeSpeed="slow">
          {media?.map(({fields}) => {
            const file = fields.asset.fields.file
            const {id, description: href} = fields
            // The image is the link's only content, so its alt text is the accessible name
            const name = fields.asset.fields.description?.trim()
            if (!file || !href || !id || !name) return null

            const dimensions = file.details?.image
            return (
              <a
                key={id}
                href={href}
                onFocus={stopPointerFocusPropagation}
                {...getAnalyticsEvent({
                  action: id,
                  tag: 'icon',
                  context: 'editors',
                  location,
                })}
              >
                <Image
                  src={file.url}
                  alt={name}
                  width={dimensions?.width ?? 40}
                  height={dimensions?.height ?? 40}
                  loading="lazy"
                />
              </a>
            )
          })}
        </LogoSuite.Logobar>
      </LogoSuite>
    </div>
  )
}
